// Teacher Home's decisions are pure: one snapshot, one clock, one explanation.
// Current engine a1/a2 counters are deliberately not used for class deadlines.
import { DOMAINS } from './mockData';
import { fieldLabel, fieldUnitTitle, isFieldUnit, studentUnitDomain } from './unitScope';
import { isStuckFact } from './definitions';

const DOMAIN_NAMES = Object.fromEntries([...DOMAINS.math, ...DOMAINS.reading].map((d) => [d.key, d.full]));
const RANK = { start: 0, stuck: 1, 'stuck-overflow': 2, pace: 3, startnext: 4, measure: 5, live: 6 };
const compareTasks = (a, b) => (a.priority ?? RANK[a.kind] ?? 9) - (b.priority ?? RANK[b.kind] ?? 9)
  || (a.kind === 'pace' && b.kind === 'pace' ? checkpointEndMs(a.cpDue) - checkpointEndMs(b.cpDue) : 0)
  || a.id.localeCompare(b.id);
const own = (o, k) => Object.prototype.hasOwnProperty.call(o || {}, k);
const validBand = (n) => Number.isInteger(n) && n >= 1 && n <= 7;
const names = (n, singular, plural) => n === 1 ? singular : plural;
const sorted = (xs) => [...xs].sort((a, b) => String(a).localeCompare(String(b)));

// Stable across property order and response ordering. Timestamps of reads are
// excluded by callers; changed evidence, rather than a new request, changes it.
export function evidenceHash(value) {
  const canonical = (v) => Array.isArray(v) ? v.map(canonical)
    : v && typeof v === 'object' ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, canonical(v[k])])) : v;
  const text = JSON.stringify(canonical(value));
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619);
  return (hash >>> 0).toString(36);
}

// Legacy date-only deadlines close at the end of their UTC day, matching the
// teacher API's slot calculation. Timed deadlines retain their exact instant.
export function checkpointEndMs(due) {
  if (typeof due !== 'string' || !due) return NaN;
  return Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(due) ? `${due}T23:59:59.999Z` : due);
}

function placementState(student, domainKey) {
  const p = student.placement;
  if (student.profileStatus !== 'ready') return 'unknown';
  if (validBand(student.domains?.[domainKey]?.band)) return 'ready';
  if (!p || p.domainKey !== domainKey || p.status === 'unknown' || p.status === 'ready') return 'unknown';
  if (p.status === 'active') return 'checkingPlacement';
  if (p.status === 'finished-unapplied') return 'missingProfile';
  if (p.status === 'missing') {
    // A failed diagnostic read cannot safely distinguish a new student from
    // an existing result whose level failed to save. A successful empty read
    // has diagnosticDone=false, even though its status also says "unknown".
    if (student.diagnosticDone === null) return 'unknown';
    return student.diagnostic?.status === 'complete' ? 'missingProfile' : 'needsPlacement';
  }
  return 'unknown';
}

function studentRows(snapshot) {
  // A duplicated enrollment must not double the concern or invent a win when
  // one duplicate disappears. Missing identities cannot be actionable rows.
  const rows = new Map();
  (snapshot?.students || []).forEach((s) => {
    if (s && typeof s.userId === 'string' && s.userId && !rows.has(s.userId)) rows.set(s.userId, s);
  });
  return [...rows.values()].sort((a, b) => a.userId.localeCompare(b.userId));
}

function normalizeSlot(slot, index, due) {
  if (!slot || slot.status === 'unknown' || slot.applies == null || (slot.applies && !slot.skipped && typeof slot.done !== 'boolean')) {
    return { index, due, applies: null, done: null, status: 'unknown' };
  }
  // A stale slot response for a moved deadline is unknown until refreshed.
  if (slot.due && checkpointEndMs(slot.due) !== checkpointEndMs(due)) {
    return { index, due, applies: null, done: null, status: 'unknown' };
  }
  return { ...slot, index, due, applies: slot.applies === true, done: slot.done === true, skipped: slot.skipped === true };
}

export function deriveHomeRecommendations(snapshot, { now = Date.now(), domainKey: requestedDomain, classId } = {}) {
  const time = Number(now instanceof Date ? now.getTime() : now);
  const unit = snapshot?.unit || null;
  // A whole-field unit (2026-10-08): the model's key is the FIELD ('math' /
  // 'reading', never a domain key, so board records and links cannot collide
  // with a domain unit's), and every per-student read uses that student's
  // own domain (`unitDomain`). Domain units: unchanged.
  const fieldUnit = isFieldUnit(unit);
  const subject = fieldUnit ? (unit.subject === 'reading' ? 'reading' : 'math') : null;
  const domainKey = fieldUnit ? subject : (unit?.domain || unit?.domainKey || snapshot?.domainKey || requestedDomain || 'algebra');
  const domainLabel = fieldUnit ? fieldUnitTitle(subject) : (DOMAIN_NAMES[domainKey] || domainKey);
  // "in Algebra" / "in their Math domain": the phrase for a student's level scope.
  const scopeLabel = fieldUnit ? `their ${fieldLabel(subject)} domain` : domainLabel;
  const domainFor = (s) => (fieldUnit ? studentUnitDomain(s, unit) : domainKey);
  const scope = `home:${encodeURIComponent(snapshot?.classID || classId || 'unknown')}:${encodeURIComponent(domainKey)}`;
  // Unknown unit identity never borrows the previous unit's checkpoint state.
  const unitKey = unit ? String(unit.unitSeq ?? unit.startDate ?? 'unknown') : null;
  const unitScope = `${scope}:unit:${encodeURIComponent(unitKey || 'none')}`;
  const rosterKnown = !snapshot?.stale && snapshot?.completeness?.roster === true;
  const unitKnown = !!snapshot && !snapshot.stale && own(snapshot, 'unit') && snapshot.completeness?.units === true;
  const unavailable = !!snapshot?.stale || !rosterKnown;
  // A retained response provides identities for frozen cards, not current
  // evidence. Invalidate its student facts as well as its source flags so a
  // failed refresh cannot promote an old instruction or establish an event.
  const roster = studentRows(snapshot).map((s) => unavailable ? {
    ...s, profileStatus: 'unknown', diagnosticDone: null, diagnostic: { status: 'unknown' },
    placement: { domainKey, status: 'unknown' }, slots: [],
  } : s);
  const statusGroups = { ready: [], needsPlacement: [], checkingPlacement: [], missingProfile: [], unknown: [] };
  const summary = {
    domainKey, domainLabel, scopeLabel, ...(fieldUnit ? { fieldUnit: true, subject } : {}), total: roster.length, ready: 0, needsPlacement: 0, checkingPlacement: 0,
    missingProfile: 0, diagnosticComplete: 0, diagnosticPartial: 0, unknown: 0,
    completedSlots: 0, applicableSlots: 0, outstandingSlots: 0, unknownSlots: 0,
  };
  const facts = { rosterKnown, unitKnown, unitKey, domainKey, ...(fieldUnit ? { fieldUnit: true, subject } : {}), students: {} };
  const dues = Array.isArray(unit?.dueDates) ? unit.dueDates : [];
  const validDues = (unit?.checkpointMode === 'continuous' ? dues.length > 0 && dues.length <= 120 : dues.length === 4)
    && dues.every((d) => Number.isFinite(checkpointEndMs(d)));
  const ownDomain = new Map(roster.map((s) => [s.userId, domainFor(s)]));
  const blockedById = new Map();
  (snapshot?.stuck?.bands || []).forEach((b) => (b.students || []).forEach((s) => {
    // A stuck entry counts for this unit when it is in the unit's domain, or,
    // in a field unit, in that student's own domain (unknown own domain: keep it).
    const wanted = fieldUnit ? ownDomain.get(s.userId) : domainKey;
    if (s.domainKey && wanted && s.domainKey !== wanted) return;
    if (!blockedById.has(s.userId)) blockedById.set(s.userId, s);
  }));

  roster.forEach((s) => {
    const placementStatus = placementState(s, domainFor(s));
    const row = { ...s, placementStatus };
    statusGroups[placementStatus].push(row);
    summary[placementStatus] += 1;
    if (s.diagnostic?.status === 'complete') summary.diagnosticComplete += 1;
    if (s.diagnostic?.status === 'partial') summary.diagnosticPartial += 1;
    const dom = s.domains?.[domainFor(s)];
    const knownProfile = s.profileStatus === 'ready';
    const blocker = blockedById.get(s.userId);
    const weak = blocker?.stuckWeak?.length ? blocker.stuckWeak : blocker?.stuckWeakTop ? [blocker.stuckWeakTop] : [];
    const stuck = knownProfile && typeof dom?.stuck === 'boolean' ? isStuckFact(dom) : blocker && knownProfile ? true : null;
    const slots = {};
    dues.forEach((due, index) => {
      const raw = (s.slots || []).find((x) => x.index === index || (x.index == null && x.key === `s${index + 1}`));
      const slot = normalizeSlot(raw, index, due);
      slots[index] = slot;
      if (slot.status === 'unknown') summary.unknownSlots += 1;
      else if (slot.applies) {
        summary.applicableSlots += 1;
        if (slot.done || slot.skipped) summary.completedSlots += 1;
        else summary.outstandingSlots += 1;
      }
    });
    facts.students[s.userId] = {
      placementStatus, ready: placementStatus === 'ready' ? true : placementStatus === 'unknown' ? null : false,
      stuck, weak: sorted(weak), slots,
    };
  });
  summary.copy = rosterKnown
    ? `${summary.ready} of ${summary.total} students have ${summary.unknown ? 'a confirmed' : 'a'} starting level in ${scopeLabel}.`
    : 'Class information is still being checked.';

  const issues = [];
  const make = (value) => {
    const students = [...(value.students || [])].sort((a, b) => a.userId.localeCompare(b.userId));
    const eventByStudent = Object.fromEntries(students.filter((s) => !unavailable && s.placementStatus !== 'unknown')
      .map((s) => [s.userId, value.eventByStudent?.[s.userId] || `${value.id}:${s.userId}`]));
    const task = {
      domainKey, domainLabel, description: '', why: '', actionLabel: 'Review', outcome: '', successText: '', sourceKeys: [],
      ...value, students,
      eventByStudent,
      uncertain: unavailable || value.uncertain || false,
      unknownStudentIds: students.filter((s) => unavailable || s.placementStatus === 'unknown').map((s) => s.userId),
      eventKeys: unavailable ? [] : sorted(value.eventKeys || Object.values(eventByStudent)),
    };
    const evidenceTask = { ...task };
    // The detail sheet receives full grouped rows, but unrelated activity or
    // profile metadata must not rewrite the board or regenerate its brief.
    delete evidenceTask.statusGroups;
    task.evidenceKey = evidenceHash({ ...evidenceTask, students: students.map((s) => ({
      userId: s.userId, name: s.name, placementStatus: s.placementStatus, checkpoint: s.checkpoint,
      band: s.domains?.[domainFor(s)]?.band, stuckSince: s.stuckSince,
    })) });
    issues.push(task);
    return task;
  };

  // Actual current blockers rank first. Stable skill identifiers, never their
  // display labels, identify the concern. Readiness is not a prerequisite.
  const groups = new Map();
  roster.forEach((s) => {
    const fact = facts.students[s.userId];
    if (fact.stuck !== true) return;
    const blocker = blockedById.get(s.userId);
    const skillId = blocker?.stuckWeak?.[0] || blocker?.stuckWeakTop || 'skill-check';
    const since = blocker?.stuckSince || s.domains?.[domainFor(s)]?.stuckSince || '';
    const row = { ...s, stuckSince: since };
    if (!groups.has(skillId)) groups.set(skillId, []);
    groups.get(skillId).push(row);
  });
  const stuckIssues = [...groups.entries()]
    .sort(([a, as], [b, bs]) => bs.length - as.length || a.localeCompare(b))
    .map(([skillId, students]) => {
      const label = snapshot?.stuck?.skillNames?.[skillId] || (skillId === 'skill-check' ? 'their skill check' : String(skillId).replace(/^BS-[A-Z]+-\d+-/, '').replace(/-/g, ' '));
      return make({
        id: `${scope}:stuck:${encodeURIComponent(skillId)}`, kind: 'stuck', skillId, students,
        title: students.length === 1 ? `${students[0].name} is stuck on ${label}` : `${students.length} students are stuck on ${label}`,
        description: 'Review their missed questions, then choose the help they need in Intervene.',
        why: 'Their skill check is blocking the next piece of work.',
        actionLabel: 'Review missed questions', outcome: 'Choose an explanation or next attempt that helps them continue.',
        successText: 'These students are no longer blocked.', sourceKeys: ['roster', 'profiles'],
        eventKeys: students.map((s) => `${s.userId}:${skillId}:${s.stuckSince}`),
        eventByStudent: Object.fromEntries(students.map((s) => [s.userId, `${s.userId}:${skillId}:${s.stuckSince}`])),
      });
    });
  const tasks = stuckIssues.slice(0, 3);
  if (stuckIssues.length > 3) {
    const extra = stuckIssues.slice(3);
    tasks.push(make({
      id: `${scope}:stuck-overflow`, kind: 'stuck-overflow', students: extra.flatMap((t) => t.students),
      underlyingIds: extra.map((t) => t.id),
      title: `${extra.length} more ${names(extra.length, 'skill has', 'skills have')} stuck students`,
      description: 'Review the remaining students and their missed questions in Intervene.',
      why: 'Their skill checks are blocking the next piece of work.',
      actionLabel: 'Review remaining students', outcome: 'Choose the help each student needs to continue.',
      successText: 'These students are no longer blocked.', sourceKeys: ['roster', 'profiles'],
      eventKeys: extra.flatMap((t) => t.eventKeys),
      eventByStudent: Object.assign({}, ...extra.map((t) => t.eventByStudent)),
    }));
  }

  const pending = roster.filter((s) => facts.students[s.userId].ready !== true)
    .map((s) => ({ ...s, placementStatus: facts.students[s.userId].placementStatus }));
  if (pending.length) {
    const knownMissing = pending.length - summary.unknown;
    const placementJobs = [];
    if (summary.needsPlacement) placementJobs.push(`${summary.needsPlacement} ${names(summary.needsPlacement, 'student needs', 'students need')} to choose “Find your level”`);
    if (summary.checkingPlacement) placementJobs.push(`${summary.checkingPlacement} ${names(summary.checkingPlacement, 'student has', 'students have')} a placement quiz to finish`);
    if (summary.missingProfile) placementJobs.push(`${summary.missingProfile} ${names(summary.missingProfile, 'student has', 'students have')} a completed assessment, but no starting level for practice`);
    if (summary.unknown) placementJobs.push(`we could not load starting levels for ${summary.unknown} ${names(summary.unknown, 'student', 'students')}`);
    tasks.push(make({
      id: `${scope}:measure`, kind: 'measure', students: pending, statusGroups,
      title: knownMissing ? `Review ${domainLabel} placement for ${pending.length} ${names(pending.length, 'student', 'students')}`
        : `Check ${domainLabel} placement information`,
      description: `${placementJobs.join('; ')}.`,
      why: summary.missingProfile
        ? 'Open the student next steps to see whose result needs help from Brain Buffs. Do not ask students to repeat a completed assessment. Other students can continue.'
        : fieldUnit ? 'Each student needs a starting level for their own domain in this unit. A placement quiz is enough; the full diagnostic is optional.'
          : 'Each student needs a starting level for this domain. A placement quiz is enough; the full diagnostic is optional.',
      actionLabel: 'Review student next steps', outcome: 'See who should start or finish a check, and whose completed result needs help from Brain Buffs.',
      successText: `Starting levels are ready in ${domainLabel}.`,
      sourceKeys: ['roster', 'profiles', 'diagnostics', 'walks'],
      uncertain: knownMissing === 0,
    }));
  }

  // Each student appears only under their earliest overdue applicable slot.
  // A missing anchor, N/A, test-out, or retake is handled by canonical slots.
  if (unitKnown && unit && validDues && unitKey !== 'unknown') {
    const claimed = new Set();
    dues.map((due, index) => ({ due, index })).sort((a, b) => checkpointEndMs(a.due) - checkpointEndMs(b.due) || a.index - b.index)
      .forEach(({ due, index }) => {
        if (!(checkpointEndMs(due) < time)) return;
        const allMissing = roster.filter((s) => {
          const slot = facts.students[s.userId].slots[index];
          return slot.applies === true && slot.done === false && !slot.skipped;
        }).map((s) => ({ ...s, checkpoint: facts.students[s.userId].slots[index] }));
        if (!allMissing.length) return;
        const students = allMissing.filter((s) => !claimed.has(s.userId));
        students.forEach((s) => claimed.add(s.userId));
        const underlying = make({
          id: `${unitScope}:pace:${index}`, kind: 'pace', cpIndex: index, cpDue: due, unitKey, students: allMissing,
          title: `${allMissing.length} ${names(allMissing.length, 'student needs', 'students need')} to finish checkpoint\u00a0${index + 1}`,
          description: `Check each student's assigned work and follow up on checkpoint ${index + 1}.`,
          why: 'This deadline has passed. The unfinished work may be placement, a lesson, practice, or a retake.',
          actionLabel: 'See unfinished work', outcome: 'Give each student a specific next step for their overdue work.',
          successText: `Checkpoint ${index + 1} has been completed or tested out.`,
          sourceKeys: ['roster', 'slots'],
        });
        if (students.length) tasks.push({ ...underlying, students,
          title: `${students.length} ${names(students.length, 'student needs', 'students need')} to finish checkpoint\u00a0${index + 1}`,
        });
      });
    if (roster.length && dues.every((d) => checkpointEndMs(d) < time)) {
      const assignedWorkComplete = rosterKnown && snapshot?.completeness?.slots === true
        && summary.applicableSlots > 0 && summary.outstandingSlots === 0 && summary.unknownSlots === 0;
      tasks.push(make({
        id: `${unitScope}:startnext`, kind: 'startnext', unitKey, students: [],
        title: `Review ${domainLabel} before the next unit`,
        description: `${dues.length === 1 ? 'The checkpoint date has' : `All ${dues.length} checkpoint dates have`} passed. ${summary.completedSlots} of ${summary.applicableSlots} known assigned checkpoints are complete${summary.unknownSlots ? '; some assignment information is still unavailable' : ''}.`,
        why: assignedWorkComplete ? 'All assigned checkpoints are complete. Review the recorded grades before closing this unit.'
          : 'A passed deadline does not mean the work is finished. Review what remains before deciding to lock grades and start the next unit.',
        actionLabel: 'Review unit and grades',
        outcome: assignedWorkComplete ? 'Lock this unit’s grades, then choose the next unit.'
          : 'Decide whether to allow more work or finish this unit and plan the next one.',
        successText: 'The next unit is ready to plan.', sourceKeys: ['roster', 'slots'],
        completedSlots: summary.completedSlots, applicableSlots: summary.applicableSlots,
        outstandingSlots: summary.outstandingSlots, unknownSlots: summary.unknownSlots,
        canFinish: rosterKnown && snapshot?.completeness?.slots === true && summary.unknownSlots === 0,
      }));
    }
  }
  if (unitKnown && !unit && rosterKnown && roster.length) {
    const scheduledUnit = snapshot.scheduledUnit || snapshot.scheduledUnits?.[0] || null;
    tasks.push(make({
      id: `${scope}:start`, kind: 'start', students: [], scheduledUnit, mode: scheduledUnit ? 'scheduled' : 'new',
      title: scheduledUnit ? 'Review your scheduled unit' : 'Choose the next unit',
      description: scheduledUnit ? 'A unit is already in the plan. Review its start date and checkpoints.'
        : summary.ready ? `You can start ${domainLabel} now. ${summary.copy}`
          : 'You can choose a unit now, then review each student’s next step before asking them to take a check.',
      why: scheduledUnit ? 'Use the existing plan before creating another unit.'
        : 'The class shares one unit, with work matched to each student. You do not need to wait for everyone to finish placement.',
      actionLabel: scheduledUnit ? 'Review scheduled unit' : 'Choose a unit',
      outcome: scheduledUnit ? 'Confirm when students start and what their first deadline will be.'
        : 'Students with a level get their work. Review who needs a check and who needs help with a completed result.',
      successText: 'A unit is now running.', sourceKeys: ['roster', 'classes'],
      eventKeys: [scheduledUnit ? `scheduled:${scheduledUnit.unitSeq ?? scheduledUnit.startAt ?? scheduledUnit.startDate ?? 'planned'}` : 'choose-unit'],
    }));
  }
  const activeCount = snapshot?.activity?.activeCount;
  if (unitKnown && unit && rosterKnown && snapshot?.completeness?.activity === true && Number.isInteger(activeCount) && activeCount > 0 && activeCount <= roster.length) {
    tasks.push(make({
      id: `${unitScope}:live`, kind: 'live', students: [], ephemeral: true,
      title: `${activeCount} ${names(activeCount, 'student is', 'students are')} practising now`,
      description: 'Recent activity is available in Live Monitor.', why: 'Open the monitor if you want to check in on the students currently working.',
      actionLabel: 'Open Live Monitor', outcome: 'See current activity and check in where help is needed.', sourceKeys: ['activity'],
    }));
  }
  tasks.sort(compareTasks);
  const complete = !!snapshot && rosterKnown && unitKnown && ['profiles', 'diagnostics', 'walks', 'slots'].every((key) => snapshot.completeness?.[key] === true)
    && summary.unknown === 0 && (!unit || (validDues && unitKey !== 'unknown' && summary.unknownSlots === 0));
  const stage = !rosterKnown || !unitKnown ? 'checking' : !roster.length ? 'empty'
    : !unit ? tasks.some((t) => t.kind === 'start' && t.scheduledUnit) ? 'scheduled' : 'ready-to-start'
      : validDues && dues.every((due) => checkpointEndMs(due) < time) ? 'review' : 'active';
  return { tasks, summary, stage, complete, issues, facts, evidenceKey: evidenceHash({ summary, stage, tasks: tasks.map((t) => t.evidenceKey), complete }) };
}

// The prominent next step and the brief must honor the same visible, current
// board choices. A dismissed/handled task or an old frozen row is not a next step.
export function selectHomeNextTask(model, board) {
  const visible = board ? new Map([...(board.todo || []), ...(board.inprogress || [])].map((t) => [t.id, t])) : null;
  return (model?.tasks || []).map((t) => visible ? visible.get(t.id) : t)
    .find((t) => t && !t.uncertain && !t.history && !t.cleared && !t.manualHandled) || null;
}

export function buildHomeBrief(model, { board, now = Date.now(), compact = false } = {}) {
  if (!model) return 'Checking your class information…';
  const { summary, complete, facts } = model;
  if (!facts?.rosterKnown) return 'Class information is still being checked. Current concerns will stay visible until their status can be confirmed.';
  if (!summary.total) return 'No students are enrolled yet. Their starting levels and next steps will appear here as they join.';
  const parts = [];
  if (model.stage === 'ready-to-start') parts.push('You can start a unit now; the class does not need to wait for everyone to finish placement.');
  else if (model.stage === 'scheduled') parts.push('Your next unit is scheduled. Review that plan before creating another unit.');
  parts.push(summary.copy);
  const today = new Date(now).toDateString();
  const progress = [...(board?.todo || []), ...(board?.inprogress || []), ...(board?.done || [])]
    .filter((t) => !t.history && t.progressCount > 0 && t.progressText && t.domainKey === summary.domainKey)
    .filter((t) => t.kind !== 'pace' || t.unitKey === facts.unitKey)
    .sort((a, b) => (b.progressAt || 0) - (a.progressAt || 0) || compareTasks(a, b))[0];
  const win = [...(board?.done || [])].filter((t) => t.cleared && t.history && t.domainKey === summary.domainKey
    && t.progressCount > 0 && t.progressText
    && (t.originalKind !== 'pace' || t.unitKey === facts.unitKey) && t.resolvedAt <= Number(now)
    && new Date(t.resolvedAt).toDateString() === today).sort((a, b) => b.resolvedAt - a.resolvedAt)[0];
  const progressSentence = !complete ? '' : progress
    ? `Confirmed progress since that task appeared: ${progress.progressText.replace(/[.!?]$/, '')}.`
    : win ? `Confirmed progress: ${win.progressText.replace(/[.!?]$/, '')}.` : '';
  if (compact) {
    const pendingCounts = [];
    if (summary.needsPlacement) pendingCounts.push(`${summary.needsPlacement} ${names(summary.needsPlacement, 'needs', 'need')} placement`);
    if (summary.checkingPlacement) pendingCounts.push(`${summary.checkingPlacement} ${names(summary.checkingPlacement, 'is', 'are')} taking placement`);
    if (summary.missingProfile) pendingCounts.push(`${summary.missingProfile} ${names(summary.missingProfile, 'student finished', 'students finished')} an assessment, but ${names(summary.missingProfile, 'their starting level is', 'their starting levels are')} missing`);
    return [summary.copy, progressSentence || (pendingCounts.length ? `${pendingCounts.join('; ')}.` : ''),
      complete ? '' : 'Some information is still being checked.'].filter(Boolean).join(' ');
  }
  if (progressSentence) parts.push(progressSentence);
  if (summary.checkingPlacement) parts.push(`${summary.checkingPlacement} ${names(summary.checkingPlacement, 'student is', 'students are')} working through a placement quiz.`);
  // scopeLabel = the domain name, or "their Math domain" in a whole-field unit.
  const levelScope = summary.scopeLabel || summary.domainLabel;
  if (summary.missingProfile) parts.push(`${summary.missingProfile} ${names(summary.missingProfile, 'student finished', 'students finished')} an assessment, but their ${levelScope === summary.domainLabel ? summary.domainLabel : `${levelScope.replace(/^their /, '')}`} ${names(summary.missingProfile, 'starting level is', 'starting levels are')} missing. Open the student next steps to refresh their results or contact Brain Buffs. Do not ask them to repeat the assessment.`);
  if (summary.needsPlacement) parts.push(`${summary.needsPlacement} ${names(summary.needsPlacement, 'student can', 'students can')} use the ${summary.fieldUnit ? `placement quiz for their own domain` : `${summary.domainLabel} placement quiz`} to get started.`);
  const first = selectHomeNextTask(model, board);
  if (first) {
    const underway = board?.inprogress?.some((t) => t.id === first.id);
    if (underway) parts.push(`You have started reviewing: ${first.title.replace(/[.!?]$/, '')}.`);
    else parts.push(`Next: ${first.title.replace(/[.!?]$/, '')}.`);
  } else if (board?.done?.some((t) => t.manualHandled && !t.cleared)) {
    parts.push('You have marked the current tasks handled. Student progress is tracked separately.');
  } else if (model.tasks.some((t) => !t.ephemeral && !t.uncertain)) {
    parts.push('Some current tasks are hidden from your board. Their underlying progress is still being tracked.');
  } else if (complete) parts.push('There are no confirmed concerns needing action right now.');
  if (!complete) parts.push('Some information is still being checked; missing information will not count as completed work.');
  return parts.join(' ');
}
